import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, Component, EventEmitter, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { IDataType, IItemOperationSetup, IPostTerminalTemplateRequest, ISortSetting, TerminalTemplateAdministrationService } from '@shared/services/terminaltemplateAdministration.service';
import { ValidatorPatterns } from '@shared/validatorpatterns';

@Component({
	selector: 'app-create-terminaltemplate',
	templateUrl: './create-terminaltemplate.component.html',
	styleUrls: ['./create-terminaltemplate.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CreateTerminalTemplateComponent implements OnInit {
	@Output() created: EventEmitter<any> = new EventEmitter();

	form: FormGroup;
	loading: boolean = false;
	terminalTemplate: IPostTerminalTemplateRequest = {
		alias: '',
		name: '',
		data: {
			detailView: '',
			groupList: [],
			unitList: [],
		},
	};

	httpError: string = '';
	dataTypes: string[] = Object.keys(IDataType).filter((key) => Number.isNaN(Number(IDataType[key])));

	sortSettings: string[] = Object.keys(ISortSetting).filter((key) => Number.isNaN(Number(ISortSetting[key])));

	constructor(
		public dialogRef: MatDialogRef<CreateTerminalTemplateComponent>,
		private terminalTemplateService: TerminalTemplateAdministrationService,
		private fb: FormBuilder,
	) {}

	ngOnInit(): void {
		this.initializeForm();
	}

	initializeForm(): void {
		this.form = this.fb.group({
			alias: [this.terminalTemplate.alias, [Validators.required, Validators.maxLength(255), Validators.pattern(ValidatorPatterns.Alias)]],
			name: [this.terminalTemplate.name, [Validators.maxLength(255), Validators.pattern(ValidatorPatterns.Description)]],
			detailView: [this.terminalTemplate.data.detailView, [Validators.maxLength(200000)]],
			groupList: this.fb.array([]),
			unitList: this.fb.array([]),
		});

		this.populateFormArrays();

		this.form.valueChanges.subscribe((value) => {
			// Loop over all top-level keys in the form's value
			Object.keys(value).forEach((key) => {
				// If it's detailView, groupList, or unitList, store it under this.terminalTemplate.data
				if (key === 'groupList' || key === 'unitList' || key === 'detailView') {
					this.terminalTemplate.data[key] = value[key];
				}
				// Else, if the key already exists on this.terminalTemplate, assign it there
				else if (key in this.terminalTemplate) {
					this.terminalTemplate[key] = value[key];
				}
			});
		});
	}

	populateFormArrays(): void {
		// First push existing groups into the group list
		this.terminalTemplate.data.groupList.forEach((existing) => {
			this.groupList.push(this.fb.group(existing));
		});

		// Push existing objets into the unit list
		if (this.terminalTemplate.data.unitList) {
			this.terminalTemplate.data.unitList.forEach((unit) => {
				this.unitList.push(this.createListItem(unit));
			});
		}
	}

	get controls() {
		return this.form.controls;
	}

	getFirstValidationErrorName(formControlName: string) {
		return Object.keys(this.form.controls[formControlName].errors)[0];
	}

	get groupList(): FormArray<FormGroup> {
		return this.form.get('groupList') as FormArray<FormGroup>;
	}

	get unitList(): FormArray<FormGroup> {
		return this.form.get('unitList') as FormArray<FormGroup>;
	}

	addUnitListItem(): void {
		this.unitList.push(
			this.createListItem({
				source: '',
				title: '',
				dataType: IDataType.String,
				format: '',
				sort: ISortSetting.None,
			}),
		);
	}

	deleteUnitListItem(index: number): void {
		this.unitList.removeAt(index);
	}

	addGroupListItem(): void {
		this.groupList.push(
			this.createListItem({
				source: '',
				title: '',
				dataType: IDataType.String,
				format: '',
				sort: ISortSetting.None,
			}),
		);
	}

	deleteGroupListItem(index: number): void {
		this.groupList.removeAt(index);
	}

	createListItem(unit: IItemOperationSetup): FormGroup {
		return this.fb.group({
			source: [unit.source, [Validators.required, Validators.maxLength(255)]],
			title: [unit.title, [Validators.required, Validators.maxLength(255)]],
			datatype: [unit.dataType, Validators.required],
			format: [unit.format],
			sort: [unit.sort, Validators.required],
		});
	}

	onDropGroup(event: CdkDragDrop<AbstractControl[]>): void {
		if (event.previousIndex === event.currentIndex) {
			return;
		}

		const movedControl = this.groupList.at(event.previousIndex);
		this.groupList.removeAt(event.previousIndex);
		this.groupList.insert(event.currentIndex, movedControl);
	}

	onDropUnit(event: CdkDragDrop<AbstractControl[]>): void {
		if (event.previousIndex === event.currentIndex) {
			return;
		}

		const movedControl = this.unitList.at(event.previousIndex);
		this.unitList.removeAt(event.previousIndex);
		this.unitList.insert(event.currentIndex, movedControl);
	}

	onCancel = () => this.dialogRef.close();

	async onSave() {
		if (this.loading) return;
		this.httpError = '';
		if (this.form.valid) {
			this.loading = true;

			this.terminalTemplate.data.groupList = this.groupList.value.filter((grp) => grp.title !== '');

			this.terminalTemplateService.createTerminalTemplate(this.terminalTemplate).subscribe({
				next: () => {
					this.created.emit();
					this.dialogRef.close();
				},
				error: (errorResponse) => {
					this.httpError = `${errorResponse.status}`;
					this.loading = false;
				},
			});
		}
	}
}
